import { get, set } from 'object-path-immutable';
import {
  SINGLE_SELECT,
  MULTI_SELECT,
  RATING_EMOJI,
  RATING_NUMBER,
  RATING_STAR,
} from 'lib/block';
import { isColorLight } from 'utils/color-handler';
import {
  addTheme as addTextTheme,
  overrides as textOverrides,
  extractStyles as extractTextStyles,
} from './text';
import {
  applyThemeInLocalizedBlock,
  calculateOverridePathsInLocalizedBlock,
  calculateOverridePaths,
  styleToThemeStyle,
  transformValueToThemeFormat,
  augmentedWrap,
} from './utils';

const PATHS = {
  root: 'style',
  colors: '',
  label: 'label',
  errorLabel: 'errorLabel',
  content: 'options.0.content',
  contentSelected: 'options.0.selectedContent',
};

// Remove the keys that are defined in the theme while leaving the structural ones
const structuralStyles = (style, isRating) => {
  if (isRating) {
    const { fontName, foregroundColor, backgroundColor, borderColor, ...rest } =
      style;
    return rest;
  }

  // Option select has fontSize as part of the theme too
  const {
    fontName,
    fontSize,
    foregroundColor,
    backgroundColor,
    borderColor,
    ...rest
  } = style;
  return rest;
};

const optionThemeAdder =
  isRating => (content, selectedVariant, preserveOverrides) =>
    set(content, 'style', {
      ...(preserveOverrides
        ? content.style
        : structuralStyles(content.style, isRating)),
      theme: selectedVariant,
    });

export const addTheme =
  optionName => (content, selectedVariant, preserveOverrides) => {
    const updated = augmentedWrap(content)
      .updateStyle(
        PATHS.root,
        {
          // SDKs require a full width for rating blocks
          ...(content.ratingType
            ? { width: -1, horizontalAlignment: 'center' }
            : { horizontalAlignment: 'leading' }),
          theme: selectedVariant,
        },
        preserveOverrides
      )
      .set('theme', `${selectedVariant}.colors`)
      // Remove special keys that are defined in the theme
      .deleteProperties(
        ['accentColor', 'selectedColor', 'unselectedColor'],
        preserveOverrides
      );

    if (content.errorLabel) {
      updated.set(
        PATHS.errorLabel,
        addTextTheme(content.errorLabel, `${selectedVariant}.errorLabel`)
      );
    }

    if (content.label) {
      updated.set(
        PATHS.label,
        addTextTheme(content.label, `${selectedVariant}.label`)
      );
    }

    if (content.options) {
      updated.set(
        'options',
        content.options.map(option => ({
          ...option,
          content: applyThemeInLocalizedBlock(
            option.content,
            `${selectedVariant}.${optionName}Content`,
            preserveOverrides,
            optionThemeAdder(!!content.ratingType)
          ),
          ...(option.selectedContent && {
            selectedContent: applyThemeInLocalizedBlock(
              option.selectedContent,
              `${selectedVariant}.${optionName}ContentSelected`,
              preserveOverrides,
              optionThemeAdder(!!content.ratingType)
            ),
          }),
        }))
      );
    }

    return updated.value();
  };

const selectOptionOverrideCalculator = ({
  compiledTheme,
  content,
  path: pathPrefix,
  optionCount,
}) => {
  const path = pathPrefix ? `${pathPrefix}.style` : 'style';
  return [
    calculateOverridePaths({
      compiledTheme,
      content,
      path,
      pathName: 'option',
      ignore: ['borderColor'],
      group: { foregroundColor: ['borderColor'] },
      pathBuilder: {
        count: optionCount,
        fn: i => `options.${i}.content.${path}`,
      },
    }),
  ].flat();
};

// Sets content and selectedContent fontName together
const ratingOptionOverrideCalculator = ({
  compiledTheme,
  content,
  path: pathPrefix,
  optionCount,
}) => {
  const path = pathPrefix ? `${pathPrefix}.style` : 'style';

  return [
    calculateOverridePaths({
      compiledTheme,
      content,
      path,
      pathName: 'option',
      allow: ['fontName'],
      pathBuilder: {
        count: optionCount,
        fn: i => [
          `options.${i}.content.${path}`,
          `options.${i}.selectedContent.${path}`,
        ],
      },
    }),
    calculateOverridePaths({
      compiledTheme,
      content,
      path,
      pathName: 'option',
      allow: ['foregroundColor', 'backgroundColor'],
      group: { foregroundColor: ['borderColor'] },
      pathBuilder: {
        count: optionCount,
        fn: i => `options.${i}.content.${path}`,
      },
    }),
  ].flat();
};

const ratingOptionSelectedOverrideCalculator = ({
  compiledTheme,
  content,
  path: pathPrefix,
  optionCount,
}) => {
  const path = pathPrefix ? `${pathPrefix}.style` : 'style';
  return calculateOverridePaths({
    compiledTheme,
    content,
    path,
    pathName: 'selectedOption',
    allow: ['foregroundColor', 'backgroundColor'],
    group: { backgroundColor: ['borderColor'] },
    pathBuilder: {
      count: optionCount,
      fn: i => `options.${i}.selectedContent.${path}`,
    },
  });
};

const optionOverrides = ({ compiledTheme, content }) => {
  const optionCount = content.options.length;
  return content.ratingType
    ? [
        calculateOverridePathsInLocalizedBlock(
          { compiledTheme, content: get(content, PATHS.content), optionCount },
          ratingOptionOverrideCalculator
        ),
        calculateOverridePathsInLocalizedBlock(
          {
            compiledTheme,
            content: get(content, PATHS.contentSelected),
            optionCount,
          },
          ratingOptionSelectedOverrideCalculator
        ),
      ].flat()
    : calculateOverridePathsInLocalizedBlock(
        { compiledTheme, content: get(content, PATHS.content), optionCount },
        selectOptionOverrideCalculator
      );
};

export const overrides = ({ compiledTheme, content }) =>
  [
    calculateOverridePaths({
      compiledTheme,
      content,
      path: PATHS.root,
      ignore: ['horizontalAlignment', 'width'],
    }),
    calculateOverridePaths({
      compiledTheme,
      content,
      path: PATHS.colors,
      allow: ['accentColor', 'selectedColor', 'unselectedColor'],
    }),
    textOverrides({
      compiledTheme,
      content,
      path: PATHS.label,
      pathName: 'label',
    }),
    textOverrides({
      compiledTheme,
      content,
      path: PATHS.errorLabel,
      pathName: 'errorLabel',
    }),
    optionOverrides({
      compiledTheme,
      content,
    }),
  ].flat();

export const extractStyles = (content, type) => {
  const selectPaths = {
    unselectedColor: ['colors.$style.unselectedColor'],
    selectedColor: ['colors.$style.selectedColor'],
    'options.0.content.style.fontName': ['optionContent.$style.fontName'],
    'options.0.content.style.fontSize': ['optionContent.$style.fontSize'],
    'options.0.content.style.foregroundColor': [
      'optionContent.$style.foregroundColor',
    ],
  };

  /* eslint-disable unicorn/consistent-function-scoping */
  const ratingPaths = ({
    selectorColorPath = 'options.0.content.style.foregroundColor',
    selectedColorPath = 'options.0.selectedContent.style.backgroundColor',
  } = {}) => ({
    [selectorColorPath]: [
      'ratingNumberContent.$style.foregroundColor',
      'ratingNumberContent.$style.borderColor',
      'ratingStarContent.$style.foregroundColor',
      'ratingEmojiContent.$style.foregroundColor',
    ],
    [selectedColorPath]: [
      'ratingNumberContentSelected.$style.backgroundColor',
      'ratingNumberContentSelected.$style.borderColor',
      'ratingStarContentSelected.$style.foregroundColor',
      'ratingEmojiContentSelected.$style.foregroundColor',
      'ratingEmojiContentSelected.$style.backgroundColor',
    ],
    'options.0.content.style.fontName': [
      'ratingNumberContent.$style.fontName',
      'ratingNumberContentSelected.$style.fontName',
    ],
  });

  const transformers = {
    [SINGLE_SELECT]: selectPaths,
    [MULTI_SELECT]: selectPaths,
    [RATING_EMOJI]: ratingPaths(),
    [RATING_NUMBER]: ratingPaths(),
    [RATING_STAR]: ratingPaths({
      selectedColorPath: 'options.0.selectedContent.style.foregroundColor',
    }),
  };

  const extractedStyles = Object.entries(transformers[type]).reduce(
    (styles, [fromPath, toPaths]) => {
      const value = get(content, fromPath);
      return value
        ? toPaths.reduce(
            (acc, path) => set(acc, path, transformValueToThemeFormat(value)),
            styles
          )
        : styles;
    },
    {}
  );

  // special case for number rating color contrast if we're starting from an emoji or star rating
  const updatedBackgroundColor =
    extractedStyles.ratingNumberContentSelected?.$style?.backgroundColor;
  if (updatedBackgroundColor) {
    extractedStyles.ratingNumberContentSelected.$style.foregroundColor = {
      light: isColorLight(updatedBackgroundColor.light) ? '#000000' : '#ffffff',
    };
  }

  return {
    $style: styleToThemeStyle({
      style: get(content, PATHS.root),
      ignore: ['horizontalAlignment', 'width'],
    }),
    label: extractTextStyles(get(content, PATHS.label)),
    errorLabel: extractTextStyles(get(content, PATHS.errorLabel)),

    // select
    colors: { $style: {} },
    optionContent: { $style: {} },

    // rating
    ratingNumberContent: { $style: {} },
    ratingNumberContentSelected: { $style: {} },
    ratingStarContent: { $style: {} },
    ratingStarContentSelected: { $style: {} },
    ratingEmojiContent: { $style: {} },
    ratingEmojiContentSelected: { $style: {} },

    ...extractedStyles,
  };
};
